import { apiClient } from '@/lib/apiClient';
import type {
  NotificationAdminAudit,
  NotificationAlertFiring,
  NotificationAlertPolicy,
  NotificationDashboardSnapshot,
  NotificationGlobalConfig,
  JourneyDefinition,
  UserNotificationOverrides,
  UserNotificationProfile,
  AlertSeverity,
  AdminAuditTargetType,
} from '../types';

// ── Dashboard ──────────────────────────────────────────────────────

export interface DashboardMetricsResponse {
  snapshot: NotificationDashboardSnapshot | null;
}

export interface DashboardTimeseriesResponse {
  snapshots: NotificationDashboardSnapshot[];
}

// ── Config ─────────────────────────────────────────────────────────

export interface ConfigResponse {
  config: NotificationGlobalConfig | null;
}

export type ConfigUpdateBody = {
  reason: string;
  patch: Partial<NotificationGlobalConfig>;
};

// ── Journeys ───────────────────────────────────────────────────────

export interface JourneysResponse {
  journeys: JourneyDefinition[];
}

// ── Alerts ─────────────────────────────────────────────────────────

export interface AlertPoliciesResponse {
  policies: NotificationAlertPolicy[];
}

export interface AlertFiringsResponse {
  firings: NotificationAlertFiring[];
}

// ── Audit ──────────────────────────────────────────────────────────

export interface AuditListParams {
  actorId?: string;
  targetType?: AdminAuditTargetType;
  targetId?: string;
  since?: string;
  limit?: number;
  cursor?: string;
}

export interface AuditListResponse {
  entries: NotificationAdminAudit[];
  nextCursor?: string | null;
}

// ── Implementation ─────────────────────────────────────────────────

export interface UpdateJourneyVariantPatch {
  variantId: string;
  title?: string;
  body?: string;
  targetRoute?: string | null;
}

export interface UpdateJourneyStepPatch {
  stepNumber: number;
  variants: UpdateJourneyVariantPatch[];
}

export interface UpdateJourneyContentBody {
  name?: string;
  description?: string;
  steps?: UpdateJourneyStepPatch[];
}

export interface RecentDecision {
  decisionId: string;
  userId: string;
  notificationType: string;
  category?: string;
  journeyId?: string;
  outcome: 'send' | 'skip' | 'defer';
  reasonCodes?: string[];
  decidedAt: string;
  deliveryStatus?: 'sent' | 'failed' | 'partial';
  /**
   * Hydrated by the receipts-as-source-of-truth path on the server when the
   * matching receipt has `status='opened'`. Always undefined on the
   * decisions-direct path (use the dedicated "Opened" filter to see opens).
   */
  opened?: boolean;
  /** Hydrated alongside `opened` from the receipt. */
  openedAt?: string;
}

export interface RecentDecisionsResponse {
  decisions: RecentDecision[];
  nextCursor: string | null;
}

export interface DecisionDetail {
  decisionId: string;
  userId: string;
  notificationType: string;
  category?: string;
  triggeredBy?: string;

  outcome: 'send' | 'skip' | 'defer';
  channel?: string;
  priority?: string;
  reasonCodes?: string[];

  variantId?: string;
  title?: string;
  body?: string;
  data?: Record<string, unknown>;

  journeyId?: string;
  journeyStep?: number;
  experimentId?: string;
  experimentVariant?: string;

  nextAttemptAt?: string;
  retryCount?: number;
  originalRequestId?: string;
  retryState?: {
    status: 'scheduled' | 'processing' | 'completed' | 'cancelled' | 'failed';
    attemptCount: number;
    lastAttemptAt?: string;
    lastError?: string;
    completedAt?: string;
    cancelReason?: string;
  };

  deliveryStatus?: 'pending' | 'sent' | 'failed' | 'partial';
  pushSuccessCount?: number;
  pushFailureCount?: number;
  inboxWritten?: boolean;

  hasUnresolvedPlaceholders?: boolean;
  missingPlaceholderKeys?: string[];

  opened?: boolean;
  openedAt?: string;
  actionTaken?: boolean;
  actionTakenAt?: string;
  actionType?: string;

  requestedAt: string;
  decidedAt: string;
  sentAt?: string;
}

export interface DecisionDetailUser {
  userId: string;
  email: string | null;
  displayName: string | null;
  timeZone: string | null;
}

export interface DecisionDetailResponse {
  decision: DecisionDetail;
  user: DecisionDetailUser | null;
}

export interface DecisionsTimeseriesDay {
  day: string;
  sends: number;
  opens: number;
  suppressions: number;
  decisions: number;
}

export interface DecisionsTimeseriesPayload {
  days: DecisionsTimeseriesDay[];
  skipReasons: Array<{ reason: string; count: number }>;
  totals: { sends: number; opens: number; suppressions: number; decisions: number };
  /** True iff the bounded decisions scan returned everything in the window. */
  decisionsScannedAll: boolean;
  /** True iff the bounded opened-receipts scan returned everything in the window. */
  opensScannedAll: boolean;
  /** Aggregate flag: decisionsScannedAll && opensScannedAll. */
  scannedAll: boolean;
  windowDays: number;
  journeyId: string | null;
}

export interface DecisionsTimeseriesResponse {
  timeseries: DecisionsTimeseriesPayload;
}

export const notificationsApi = {
  getDashboardMetrics: (day?: string) =>
    apiClient.get<DashboardMetricsResponse>('/notifications/dashboard/metrics', { day }),

  getDashboardTimeseries: (days = 14) =>
    apiClient.get<DashboardTimeseriesResponse>('/notifications/dashboard/timeseries', { days }),

  refreshDashboardSnapshot: () =>
    apiClient.post<DashboardMetricsResponse>('/notifications/dashboard/refresh'),

  listRecentDecisions: (
    params: {
      limit?: number;
      outcome?: 'send' | 'skip' | 'defer';
      userId?: string;
      opened?: boolean;
      cursor?: string;
    } = {},
  ) => apiClient.get<RecentDecisionsResponse>('/notifications/decisions', params),

  getDecisionDetail: (decisionId: string) =>
    apiClient.get<DecisionDetailResponse>(
      `/notifications/decisions/${encodeURIComponent(decisionId)}`,
    ),

  getDecisionsTimeseries: (params: { days?: number; journeyId?: string } = {}) =>
    apiClient.get<DecisionsTimeseriesResponse>('/notifications/decisions/timeseries', params),

  getConfig: () => apiClient.get<ConfigResponse>('/notifications/config'),

  updateConfig: (body: ConfigUpdateBody) =>
    apiClient.put<{ config: NotificationGlobalConfig }>('/notifications/config', body),

  listJourneys: () => apiClient.get<JourneysResponse>('/notifications/journeys/all'),

  killJourney: (journeyId: string, reason: string) =>
    apiClient.post<{ journey: JourneyDefinition }>(
      `/notifications/journeys/${encodeURIComponent(journeyId)}/kill`,
      { reason },
    ),

  reviveJourney: (journeyId: string, reason?: string) =>
    apiClient.post<{ journey: JourneyDefinition }>(
      `/notifications/journeys/${encodeURIComponent(journeyId)}/revive`,
      { reason },
    ),

  updateJourneyContent: (journeyId: string, body: UpdateJourneyContentBody) =>
    apiClient.put<{ journey: JourneyDefinition }>(
      `/notifications/journeys/${encodeURIComponent(journeyId)}/content`,
      body,
    ),

  getUserProfile: (userId: string) =>
    apiClient.get<UserNotificationProfile>(
      `/notifications/users/${encodeURIComponent(userId)}/profile`,
    ),

  setUserOverride: (
    userId: string,
    body: { reason: string; overrides: Partial<UserNotificationOverrides> },
  ) =>
    apiClient.put<{ overrides: UserNotificationOverrides }>(
      `/notifications/users/${encodeURIComponent(userId)}/override`,
      body,
    ),

  clearUserOverride: (userId: string, reason: string) =>
    // DELETE with no body — `reason` travels in the query string because
    // some proxies strip DELETE request bodies. Backend validates via
    // `clearUserOverrideQuerySchema`.
    apiClient.delete<{ cleared: true }>(
      `/notifications/users/${encodeURIComponent(userId)}/override`,
      undefined,
      { reason },
    ),

  listAlertPolicies: () =>
    apiClient.get<AlertPoliciesResponse>('/notifications/alerts/policies'),

  updateAlertPolicy: (
    policyKey: string,
    body: { reason: string; patch: Partial<NotificationAlertPolicy> },
  ) =>
    apiClient.put<{ policy: NotificationAlertPolicy }>(
      `/notifications/alerts/policies/${encodeURIComponent(policyKey)}`,
      body,
    ),

  listFirings: (params: {
    policyKey?: string;
    severity?: AlertSeverity;
    since?: string;
    limit?: number;
  }) => apiClient.get<AlertFiringsResponse>('/notifications/alerts/firings', params),

  acknowledgeFiring: (firingId: string, note?: string) =>
    apiClient.post<{ firing: NotificationAlertFiring }>(
      `/notifications/alerts/firings/${encodeURIComponent(firingId)}/ack`,
      { note },
    ),

  listAudit: (params: AuditListParams) =>
    apiClient.get<AuditListResponse>('/notifications/audit', { ...params }),
};
